/**
 * In-memory estimates for the mock API client (tracker item 39). Kept out of
 * mock.ts: MockAPIClient hands it a small host (its users, customers,
 * properties, items, jobs and schedules) and delegates the IEstimatesApi
 * methods. Same rules as the backend where the UI depends on them: statuses
 * and their 409 codes, sequential numbers per tenant, DRAFT-only edits, send /
 * resend with a public link token, approve / decline once (portal, link,
 * office), expiry, cancel, conversion to a DRAFT job or a recurring schedule,
 * and tenant / customer isolation. Not modelled: discounts (none applied),
 * price charts (catalog or manual price), emails.
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';
import type { MockBundleTemplate } from './mockBundles';

function apiError(status: number, message: string, code?: string, details?: unknown): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = {
    status, statusText: '', headers: {}, config,
    data: { success: false, message, ...(code ? { code } : {}), ...(details !== undefined ? { details } : {}) },
  } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

const invalid = (message: string) => apiError(400, message, 'VALIDATION_ERROR');
const notFound = (message = 'Estimate not found') => apiError(404, message, 'NOT_FOUND');
const conflict = (message: string, code: string, details?: unknown) => apiError(409, message, code, details);

function money(cents: number): string {
  return (cents / 100).toFixed(2);
}

function toCents(value: string | number): number {
  return Math.round(Number(value) * 100);
}

function todayYmd(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function plusDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** What the mock estimates read and write in the rest of the mock. */
export interface MockEstimateHost {
  /** ADMIN / OFFICE_MANAGER only, else 403 */
  office(): { id: string; email: string; tenantId: string };
  /** A linked CUSTOMER_PORTAL login, else 403 */
  portalCustomer(): { tenantId: string; customerId: string; userId: string; name: string };
  customer(tenantId: string, id: string): { id: string; name: string; email?: string | null; taxExempt?: boolean } | undefined;
  property(tenantId: string, id: string): { id: string; customerId: string; address: string; city: string; state: string; zip: string } | undefined;
  item(tenantId: string, id: string): { id: string; name: string; unitPrice: string; taxable: boolean } | undefined;
  /** The property's tax rate as a fraction (0.0625) */
  taxRate(tenantId: string, propertyId: string): number;
  companyName(tenantId: string): string;
  createJob(data: types.CreateJobRequest): Promise<{ id: string }>;
  addJobLineItem(jobId: string, data: types.AddJobLineItemRequest): Promise<unknown>;
  createRecurringSchedule(data: types.CreateRecurringScheduleRequest): Promise<{ id: string }>;
  /** Service bundles (tracker item 85): the bundle with its visit offsets, or null when it does not exist in the tenant */
  bundleTemplate(tenantId: string, bundleId: string): MockBundleTemplate | null;
  /** One enrollment (visit jobs included) from an estimate's remaining bundle lines, at the quoted prices */
  createEnrollmentFromEstimate(tenantId: string, input: {
    propertyId: string; bundleId: string | null; bundleName: string; startDate: string; assignedTo: string | null;
    lines: Array<{ itemId: string; quantity: string; unitPrice: string; offsetDays: number }>;
  }): { id: string };
}

type MockEstimate = types.Estimate & { tenantId: string; tokens: string[] };

export class MockEstimates {
  private estimates: MockEstimate[] = [];
  private numbers = new Map<string, number>();
  private tokenCounter = 0;

  constructor(private readonly host: MockEstimateHost) {}

  /** The last link token issued for an estimate (tests: what the email would carry). */
  lastToken(id: string): string | null {
    const found = this.estimates.find((e) => e.id === id);
    return found?.tokens[found.tokens.length - 1] ?? null;
  }

  private status(e: MockEstimate): types.EstimateStatus {
    return e.status === 'SENT' && e.expiryDate < todayYmd() ? 'EXPIRED' : e.status;
  }

  private view(e: MockEstimate): types.Estimate {
    const { tenantId, tokens, ...rest } = e;
    return { ...rest, status: this.status(e), lineItems: e.lineItems.map((l) => ({ ...l })), bundles: this.groups(e.lineItems), discounts: [] };
  }

  private find(tenantId: string, id: string): MockEstimate {
    const found = this.estimates.find((e) => e.id === id && e.tenantId === tenantId);
    if (!found) throw notFound();
    return found;
  }

  private price(tenantId: string, propertyId: string, customerTaxExempt: boolean, lines: types.EstimateLineInput[] = []): types.EstimateLine[] {
    if (!Array.isArray(lines)) throw invalid('lineItems: must be a list');
    const rate = this.host.taxRate(tenantId, propertyId);
    return lines.map((line, index) => {
      const item = this.host.item(tenantId, line.itemId);
      if (!item) throw apiError(404, `lineItems[${index}].itemId: item not found`, 'NOT_FOUND');
      const quantity = Number(line.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) throw invalid(`lineItems[${index}].quantity: must be greater than 0`);
      const manual = line.unitPrice !== undefined && line.unitPrice !== null && line.unitPrice !== '';
      if (manual && (!Number.isFinite(Number(line.unitPrice)) || Number(line.unitPrice) < 0)) throw invalid(`lineItems[${index}].unitPrice: cannot be negative`);
      const unitCents = toCents(manual ? (line.unitPrice as string | number) : item.unitPrice);
      const amount = Math.round(quantity * unitCents);
      const taxRate = item.taxable && !customerTaxExempt ? rate : 0;
      const tax = Math.round(amount * taxRate);
      const bundle = this.bundleFields(tenantId, line, index);
      return {
        id: `estline-${index}-${Math.random().toString(36).slice(2, 8)}`,
        itemId: item.id,
        itemName: item.name,
        description: line.description?.trim() || item.name,
        quantity: quantity.toFixed(2),
        unitPrice: money(unitCents),
        pricingModel: line.pricingModel ?? 'PER_ITEM',
        priceSource: manual ? 'MANUAL' : 'CATALOG',
        taxRate: String(taxRate),
        lineAmount: money(amount),
        lineDiscount: '0.00',
        lineTax: money(tax),
        lineTotal: money(amount + tax),
        ...bundle,
      };
    });
  }

  /**
   * A bundle line is a line with bundleOffsetDays. Its group is bundleId (checked in the tenant, name filled from the bundle)
   * or, for a bundle deleted since, the bundleName snapshot. Without an offset the bundle fields are refused.
   */
  private bundleFields(tenantId: string, line: types.EstimateLineInput, index: number): Pick<types.EstimateLine, 'bundleId' | 'bundleName' | 'bundleOffsetDays' | 'bundleStartDate'> {
    const plain = { bundleId: null, bundleName: null, bundleOffsetDays: null, bundleStartDate: null };
    const offset = line.bundleOffsetDays;
    const hasGroup = !!line.bundleId || !!line.bundleName;
    if (offset === undefined || offset === null) {
      if (hasGroup) throw invalid(`lineItems[${index}].bundleOffsetDays: is required for a bundle line`);
      return plain;
    }
    if (!Number.isInteger(offset) || offset < 0) throw invalid(`lineItems[${index}].bundleOffsetDays: must be a whole number of days, 0 or more`);
    if (!hasGroup) throw invalid(`lineItems[${index}].bundleId: a bundle line needs its bundle`);
    const start = line.bundleStartDate ?? null;
    if (start !== null && !/^\d{4}-\d{2}-\d{2}$/.test(start)) throw invalid(`lineItems[${index}].bundleStartDate: must be a date (YYYY-MM-DD)`);
    if (line.bundleId) {
      const template = this.host.bundleTemplate(tenantId, line.bundleId);
      if (!template) throw apiError(404, `lineItems[${index}].bundleId: bundle not found`, 'NOT_FOUND');
      return { bundleId: template.id, bundleName: template.name, bundleOffsetDays: offset, bundleStartDate: start };
    }
    return { bundleId: null, bundleName: String(line.bundleName).trim(), bundleOffsetDays: offset, bundleStartDate: start };
  }

  /** The bundle groups of the lines, in order of first appearance. */
  private groups(lines: types.EstimateLine[]): types.EstimateBundleGroup[] {
    const found = new Map<string, types.EstimateLine[]>();
    lines.filter((l) => l.bundleOffsetDays !== null).forEach((l) => {
      const key = l.bundleId ?? `name:${l.bundleName}`;
      found.set(key, [...(found.get(key) ?? []), l]);
    });
    const sum = (rows: types.EstimateLine[], pick: (l: types.EstimateLine) => string) => money(rows.reduce((total, l) => total + toCents(pick(l)), 0));
    return [...found.values()].map((rows) => ({
      bundleId: rows[0].bundleId, bundleName: rows[0].bundleName ?? '', startDate: rows[0].bundleStartDate, lineCount: rows.length, lineIds: rows.map((l) => l.id),
      subtotal: sum(rows, (l) => l.lineAmount), totalDiscount: sum(rows, (l) => l.lineDiscount), totalTax: sum(rows, (l) => l.lineTax), total: sum(rows, (l) => l.lineTotal),
    }));
  }

  private totals(lines: types.EstimateLine[]): Pick<types.Estimate, 'subtotal' | 'totalDiscount' | 'totalTax' | 'total'> {
    const sub = lines.reduce((s, l) => s + toCents(l.lineAmount), 0);
    const tax = lines.reduce((s, l) => s + toCents(l.lineTax), 0);
    return { subtotal: money(sub), totalDiscount: '0.00', totalTax: money(tax), total: money(sub + tax) };
  }

  private expiry(value: string | null | undefined, fallback: string | null): string {
    const today = todayYmd();
    if (value === undefined || value === null || value === '') {
      if (fallback) return fallback;
      return plusDays(today, 30);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw invalid('expiryDate: must be a date (YYYY-MM-DD)');
    if (value < today) throw invalid('expiryDate: cannot be in the past');
    return value;
  }

  // ================================================================ office

  async list(query: types.EstimateListQuery = {}): Promise<types.EstimatePage<types.EstimateSummary>> {
    const { tenantId } = this.host.office();
    const search = (query.search ?? '').trim().toLowerCase();
    const rows = this.estimates
      .filter((e) => e.tenantId === tenantId)
      .filter((e) => !query.status || this.status(e) === query.status)
      .filter((e) => !query.customerId || e.customer.id === query.customerId)
      .filter((e) => !query.propertyId || e.property.id === query.propertyId)
      .filter((e) => !search || [e.estimateNumber, e.title ?? '', e.customer.name, e.property.address].some((v) => v.toLowerCase().includes(search)))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.estimateNumber.localeCompare(a.estimateNumber));
    const page = query.page ?? 1;
    const limit = query.limit ?? 20;
    const data = rows.slice((page - 1) * limit, page * limit).map((e) => {
      const { lineItems, discounts, customerMessage, internalNotes, ...rest } = this.view(e);
      return { ...rest, lineCount: lineItems.length };
    });
    return { data, pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) } };
  }

  async get(id: string): Promise<types.Estimate> {
    const { tenantId } = this.host.office();
    return this.view(this.find(tenantId, id));
  }

  async preview(data: types.EstimateInput): Promise<types.EstimatePreview> {
    const { tenantId } = this.host.office();
    const property = this.host.property(tenantId, data?.propertyId);
    if (!data?.propertyId) throw invalid('propertyId: is required');
    if (!property) throw notFound('Property not found');
    const customer = this.host.customer(tenantId, property.customerId);
    const lines = this.price(tenantId, property.id, !!customer?.taxExempt, data.lineItems ?? []);
    return { lineItems: lines.map(({ id, ...rest }) => rest), discounts: [], applicableDiscounts: [], ...this.totals(lines) };
  }

  async create(data: types.EstimateInput): Promise<types.Estimate> {
    const actor = this.host.office();
    const { tenantId } = actor;
    if (!data?.propertyId) throw invalid('propertyId: is required');
    const property = this.host.property(tenantId, data.propertyId);
    if (!property) throw notFound('Property not found');
    const customer = this.host.customer(tenantId, property.customerId);
    const lines = this.price(tenantId, property.id, !!customer?.taxExempt, data.lineItems ?? []);
    const next = (this.numbers.get(tenantId) ?? 0) + 1;
    this.numbers.set(tenantId, next);
    const now = new Date().toISOString();
    const estimate: MockEstimate = {
      id: `est-${tenantId}-${next}-${Math.random().toString(36).slice(2, 8)}`,
      tenantId,
      tokens: [],
      estimateNumber: `EST-${String(next).padStart(4, '0')}`,
      status: 'DRAFT',
      customer: { id: property.customerId, name: customer?.name ?? '', email: customer?.email ?? null },
      property: { id: property.id, address: property.address, city: property.city, state: property.state, zip: property.zip },
      title: data.title?.trim() || null,
      customerMessage: data.customerMessage?.trim() || null,
      internalNotes: data.internalNotes?.trim() || null,
      expiryDate: this.expiry(data.expiryDate, null),
      lineItems: lines,
      discounts: [],
      ...this.totals(lines),
      sentAt: null,
      lastSentAt: null,
      sendCount: 0,
      respondedAt: null,
      respondedVia: null,
      responderName: null,
      responseComment: null,
      convertedAt: null,
      convertedJobId: null,
      convertedScheduleId: null,
      convertedEnrollmentIds: [],
      bundles: [],
      cancelledAt: null,
      cancelReason: null,
      createdById: actor.id,
      createdAt: now,
      updatedAt: now,
    };
    this.estimates.push(estimate);
    return this.view(estimate);
  }

  async update(id: string, data: types.EstimateUpdateInput): Promise<types.Estimate> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    if (e.status !== 'DRAFT') throw conflict(`Only a DRAFT estimate can be edited (this one is ${this.status(e)})`, 'ESTIMATE_NOT_EDITABLE');
    if (data && 'propertyId' in data) throw invalid('propertyId: cannot be changed; create a new estimate');
    if (data.title !== undefined) e.title = data.title?.trim() || null;
    if (data.customerMessage !== undefined) e.customerMessage = data.customerMessage?.trim() || null;
    if (data.internalNotes !== undefined) e.internalNotes = data.internalNotes?.trim() || null;
    if (data.expiryDate !== undefined && data.expiryDate !== null && data.expiryDate !== '') e.expiryDate = this.expiry(data.expiryDate, null);
    if (data.lineItems !== undefined) {
      const customer = this.host.customer(tenantId, e.customer.id);
      e.lineItems = this.price(tenantId, e.property.id, !!customer?.taxExempt, data.lineItems);
      Object.assign(e, this.totals(e.lineItems));
    }
    e.updatedAt = new Date().toISOString();
    return this.view(e);
  }

  async send(id: string, data: types.EstimateSendInput = {}): Promise<types.EstimateSendResult> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    const status = this.status(e);
    if (!['DRAFT', 'SENT', 'EXPIRED'].includes(status)) throw conflict(`A ${status} estimate cannot be sent`, 'ESTIMATE_NOT_SENDABLE');
    if (e.lineItems.length === 0) throw apiError(400, 'Add at least one line before sending the estimate', 'ESTIMATE_EMPTY');
    const expiry = data.expiryDate ? this.expiry(data.expiryDate, null) : e.expiryDate;
    if (expiry < todayYmd()) throw invalid('expiryDate: the estimate is past its expiry date; give a new one to send it');
    const now = new Date().toISOString();
    Object.assign(e, { status: 'SENT', expiryDate: expiry, sentAt: e.sentAt ?? now, lastSentAt: now, sendCount: e.sendCount + 1, updatedAt: now });
    this.tokenCounter += 1;
    e.tokens.push(`mocktoken${String(this.tokenCounter).padStart(4, '0')}${'x'.repeat(30)}`.slice(0, 43));
    const email = e.customer.email ?? null;
    return {
      estimate: this.view(e),
      created: true,
      message: email
        ? { id: `msg-${this.tokenCounter}`, status: 'PENDING', skipReason: null, skipReasonText: null, toAddress: email }
        : { id: `msg-${this.tokenCounter}`, status: 'SKIPPED', skipReason: 'NO_ADDRESS', skipReasonText: 'The customer has no email address', toAddress: null },
    };
  }

  private respond(e: MockEstimate, decision: types.EstimateDecision, via: types.EstimateResponseChannel, name: string | null, comment: string | null): void {
    const status = this.status(e);
    if (status === 'EXPIRED') throw conflict('This estimate has expired', 'ESTIMATE_EXPIRED');
    if (status !== 'SENT') throw conflict('This estimate was already answered', 'ESTIMATE_ALREADY_ANSWERED');
    Object.assign(e, {
      status: decision,
      respondedAt: new Date().toISOString(),
      respondedVia: via,
      responderName: name,
      responseComment: comment?.trim() || null,
      updatedAt: new Date().toISOString(),
    });
  }

  async record(id: string, decision: types.EstimateDecision, data: types.EstimateResponseInput = {}): Promise<types.Estimate> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    this.respond(e, decision, 'OFFICE', data.name?.trim() || null, data.comment ?? null);
    return this.view(e);
  }

  async cancel(id: string, reason?: string | null): Promise<types.Estimate> {
    const actor = this.host.office();
    const e = this.find(actor.tenantId, id);
    const status = this.status(e);
    if (!['DRAFT', 'SENT', 'EXPIRED', 'APPROVED'].includes(status)) throw conflict(`A ${status} estimate cannot be cancelled`, 'ESTIMATE_NOT_CANCELLABLE');
    Object.assign(e, { status: 'CANCELLED', cancelledAt: new Date().toISOString(), cancelReason: reason?.trim() || null, tokens: [] });
    return this.view(e);
  }

  // ---- service bundles (tracker item 85)

  /** POST /estimates/:id/bundles: one priced line per eligible component, grouped under the bundle (DRAFT only). */
  async addBundle(id: string, data: { bundleId: string; startDate?: string | null }): Promise<types.EstimateBundleAddResult> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    if (!data?.bundleId) throw invalid('bundleId: is required');
    if (e.status !== 'DRAFT') throw conflict(`Only a DRAFT estimate can be edited (this one is ${this.status(e)})`, 'ESTIMATE_NOT_EDITABLE');
    const start = data.startDate ?? null;
    if (start !== null && !/^\d{4}-\d{2}-\d{2}$/.test(start)) throw invalid('startDate: must be a date (YYYY-MM-DD)');
    const template = this.host.bundleTemplate(tenantId, data.bundleId);
    if (!template) throw notFound('Service bundle not found');
    if (e.lineItems.some((l) => l.bundleId === template.id)) throw conflict('This bundle is already on the estimate', 'BUNDLE_ALREADY_ON_ESTIMATE');
    if (!template.isActive) throw apiError(422, 'This bundle is inactive', 'BUNDLE_INACTIVE');
    const usable = template.components.filter((c) => c.available);
    const skipped = template.components.filter((c) => !c.available).map((c) => ({ componentId: c.id, sortOrder: c.sortOrder, itemId: c.itemId, itemName: c.itemName, reason: c.reason ?? 'ITEM_DELETED' }));
    if (usable.length === 0) throw apiError(400, 'None of the items in this bundle can be scheduled', 'BUNDLE_EMPTY', { skipped });
    const customer = this.host.customer(tenantId, e.customer.id);
    const lines = this.price(tenantId, e.property.id, !!customer?.taxExempt, usable.map((c) => ({
      itemId: c.itemId, quantity: c.quantity, bundleId: template.id, bundleOffsetDays: c.offsetDays, bundleStartDate: start,
    })));
    e.lineItems = [...e.lineItems, ...lines];
    Object.assign(e, this.totals(e.lineItems), { updatedAt: new Date().toISOString() });
    return { ...this.view(e), skipped };
  }

  /** PATCH /estimates/:id/bundles/:bundleId { startDate | null } (DRAFT only) */
  async updateBundle(id: string, bundleId: string, data: { startDate: string | null }): Promise<types.Estimate> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    if (!data || !('startDate' in data)) throw invalid('startDate: is required (a date, or null to clear it)');
    if (e.status !== 'DRAFT') throw conflict(`Only a DRAFT estimate can be edited (this one is ${this.status(e)})`, 'ESTIMATE_NOT_EDITABLE');
    if (data.startDate !== null && !/^\d{4}-\d{2}-\d{2}$/.test(String(data.startDate))) throw invalid('startDate: must be a date (YYYY-MM-DD)');
    const rows = e.lineItems.filter((l) => l.bundleId === bundleId);
    if (rows.length === 0) throw notFound('That bundle is not on this estimate');
    rows.forEach((l) => { l.bundleStartDate = data.startDate; });
    e.updatedAt = new Date().toISOString();
    return this.view(e);
  }

  /** DELETE /estimates/:id/bundles/:bundleId: removes the bundle's lines (DRAFT only) */
  async removeBundle(id: string, bundleId: string): Promise<types.Estimate> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    if (e.status !== 'DRAFT') throw conflict(`Only a DRAFT estimate can be edited (this one is ${this.status(e)})`, 'ESTIMATE_NOT_EDITABLE');
    if (!e.lineItems.some((l) => l.bundleId === bundleId)) throw notFound('That bundle is not on this estimate');
    e.lineItems = e.lineItems.filter((l) => l.bundleId !== bundleId);
    Object.assign(e, this.totals(e.lineItems), { updatedAt: new Date().toISOString() });
    return this.view(e);
  }

  /**
   * Convert an APPROVED estimate. Plain lines become one DRAFT job (or the template of a recurring schedule); every bundle on the
   * estimate becomes an enrollment from its remaining lines. A bundle without a start date needs the body's `startDate` (409
   * BUNDLE_START_DATE_REQUIRED, nothing written). A bundle-only estimate creates no job and cannot be RECURRING.
   */
  async convert(id: string, data: types.EstimateConvertInput): Promise<types.EstimateConversionResult> {
    const { tenantId } = this.host.office();
    const e = this.find(tenantId, id);
    if (data?.target !== 'JOB' && data?.target !== 'RECURRING') throw invalid('target: one of JOB, RECURRING');
    if (e.status === 'CONVERTED') throw conflict('This estimate was already converted', 'ESTIMATE_ALREADY_CONVERTED');
    if (e.status !== 'APPROVED') throw conflict(`Only an APPROVED estimate can be converted (this one is ${this.status(e)})`, 'ESTIMATE_NOT_APPROVED');
    if (data.startDate !== undefined && !/^\d{4}-\d{2}-\d{2}$/.test(String(data.startDate))) throw invalid('startDate: must be a date (YYYY-MM-DD)');
    if (data.target === 'RECURRING' && (!data.recurring?.assignedTo || !data.recurring.startDate)) {
      throw invalid('recurring.assignedTo: a recurring schedule needs an assigned technician');
    }
    const plain = e.lineItems.filter((l) => l.bundleOffsetDays === null);
    const groups = this.groups(e.lineItems);
    if (data.target === 'RECURRING' && plain.length === 0 && groups.length > 0) {
      throw invalid('target: an estimate of bundle lines only cannot become a recurring schedule');
    }
    const missing = groups.filter((g) => !data.startDate && !g.startDate);
    if (missing.length > 0) {
      throw conflict('Choose the date the bundle starts', 'BUNDLE_START_DATE_REQUIRED', { bundles: missing.map((g) => ({ bundleId: g.bundleId, bundleName: g.bundleName })) });
    }
    // Everything is checked before anything is created (the real conversion is one transaction)
    const unavailable = e.lineItems.find((l) => l.bundleOffsetDays !== null && !this.host.item(tenantId, l.itemId));
    if (unavailable) throw apiError(422, 'A visit item is no longer available', 'ENROLLMENT_ITEM_UNAVAILABLE', { itemId: unavailable.itemId });
    const assignedTo = data.target === 'RECURRING' ? data.recurring!.assignedTo : (data.job?.assignedTo ?? null);
    let jobId: string | null = null;
    let scheduleId: string | null = null;
    if (plain.length > 0 || groups.length === 0) {
      const job = await this.host.createJob({
        propertyId: e.property.id,
        notes: `From estimate ${e.estimateNumber}${e.title ? `: ${e.title}` : ''}`,
        assignedTo,
        scheduledDate: data.target === 'RECURRING' ? null : (data.job?.scheduledDate ?? null),
      } as types.CreateJobRequest);
      jobId = job.id;
      for (const line of plain) {
        await this.host.addJobLineItem(job.id, { itemId: line.itemId, quantity: line.quantity, unitPrice: line.unitPrice, pricingModel: line.pricingModel } as types.AddJobLineItemRequest);
      }
    }
    if (data.target === 'RECURRING' && jobId) {
      const r = data.recurring!;
      const schedule = await this.host.createRecurringSchedule({
        propertyId: e.property.id,
        templateJobId: jobId,
        frequency: r.frequency,
        interval: r.interval ?? 1,
        frequencyDetails: r.frequencyDetails,
        startDate: r.startDate,
        endDate: r.endDate ?? undefined,
        notes: r.notes ?? `From estimate ${e.estimateNumber}`,
      } as types.CreateRecurringScheduleRequest);
      scheduleId = schedule.id;
    }
    const enrollmentIds: string[] = [];
    for (const group of groups) {
      const rows = e.lineItems.filter((l) => (group.bundleId ? l.bundleId === group.bundleId : l.bundleId === null && l.bundleName === group.bundleName) && l.bundleOffsetDays !== null);
      const created = this.host.createEnrollmentFromEstimate(tenantId, {
        propertyId: e.property.id, bundleId: group.bundleId, bundleName: group.bundleName, startDate: data.startDate ?? group.startDate!, assignedTo,
        lines: rows.map((l) => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice, offsetDays: l.bundleOffsetDays! })),
      });
      enrollmentIds.push(created.id);
    }
    Object.assign(e, { status: 'CONVERTED', convertedAt: new Date().toISOString(), convertedJobId: jobId, convertedScheduleId: scheduleId, convertedEnrollmentIds: enrollmentIds });
    return { estimate: this.view(e), jobId, scheduleId, enrollmentIds };
  }

  // ================================================================ customer views

  private customerView(e: MockEstimate): types.CustomerEstimateView {
    const status = this.status(e);
    return {
      estimateNumber: e.estimateNumber,
      status,
      title: e.title,
      customerMessage: e.customerMessage,
      customerName: e.customer.name,
      propertyAddress: `${e.property.address}, ${e.property.city}, ${e.property.state}`,
      issueDate: e.sentAt ? e.sentAt.slice(0, 10) : null,
      expiryDate: e.expiryDate,
      lines: e.lineItems.map((l) => ({
        description: l.description, quantity: l.quantity, unitPrice: l.unitPrice, amount: l.lineAmount, discount: l.lineDiscount, tax: l.lineTax,
        bundleName: l.bundleName, bundleOffsetDays: l.bundleOffsetDays,
      })),
      discounts: [],
      subtotal: e.subtotal,
      totalDiscount: e.totalDiscount,
      totalTax: e.totalTax,
      total: e.total,
      respondedAt: e.respondedAt,
      responseComment: e.responseComment,
      canRespond: status === 'SENT',
    };
  }

  private visibleToCustomer(e: MockEstimate): boolean {
    return e.status !== 'DRAFT' && e.status !== 'CANCELLED';
  }

  async listPortal(query: { page?: number; limit?: number } = {}): Promise<types.EstimatePage<types.PortalEstimate>> {
    const scope = this.host.portalCustomer();
    const rows = this.estimates
      .filter((e) => e.tenantId === scope.tenantId && e.customer.id === scope.customerId && this.visibleToCustomer(e))
      .sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''));
    const page = query.page ?? 1;
    const limit = query.limit ?? 20;
    return {
      data: rows.slice((page - 1) * limit, page * limit).map((e) => ({ id: e.id, propertyId: e.property.id, ...this.customerView(e) })),
      pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) },
    };
  }

  private portalEstimate(id: string): { e: MockEstimate; scope: ReturnType<MockEstimateHost['portalCustomer']> } {
    const scope = this.host.portalCustomer();
    const e = this.estimates.find((x) => x.id === id && x.tenantId === scope.tenantId && x.customer.id === scope.customerId && this.visibleToCustomer(x));
    if (!e) throw notFound();
    return { e, scope };
  }

  async getPortal(id: string): Promise<types.PortalEstimate> {
    const { e } = this.portalEstimate(id);
    return { id: e.id, propertyId: e.property.id, ...this.customerView(e) };
  }

  async respondPortal(id: string, decision: types.EstimateDecision, comment?: string | null): Promise<types.PortalEstimate> {
    const { e, scope } = this.portalEstimate(id);
    this.respond(e, decision, 'PORTAL', scope.name, comment ?? null);
    return { id: e.id, propertyId: e.property.id, ...this.customerView(e) };
  }

  private byToken(token: string): MockEstimate {
    const e = this.estimates.find((x) => x.tokens.includes(token) && this.visibleToCustomer(x));
    if (!e) throw apiError(404, 'Not found');
    return e;
  }

  private publicView(e: MockEstimate): types.PublicEstimateView {
    return {
      company: {
        companyName: this.host.companyName(e.tenantId),
        logoUrl: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
        phone: null,
        email: null,
        website: null,
        footerNote: null,
      },
      ...this.customerView(e),
    };
  }

  async getPublic(token: string): Promise<types.PublicEstimateView> {
    return this.publicView(this.byToken(token));
  }

  async respondPublic(token: string, decision: types.EstimateDecision, data: types.EstimateResponseInput): Promise<types.PublicEstimateView> {
    const e = this.byToken(token);
    const name = data?.name?.trim() || null;
    if (decision === 'APPROVED' && !name) throw invalid('name: type your name to approve the estimate');
    this.respond(e, decision, 'LINK', name, data?.comment ?? null);
    return this.publicView(e);
  }
}
